const escapeHtml = value => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const PRINT_STYLES = `
  @page {
    size: A4;
    margin: 18mm 18mm 20mm;
    @bottom-right {
      content: "Página " counter(page) " de " counter(pages);
      font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 8pt;
      color: #6b7280;
    }
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    color: #1f2937;
    font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.55;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  @media screen {
    body { background: #e5e7eb; padding: 24px 0; }
    .sheet {
      background: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, .15);
      margin: 0 auto;
      padding: 18mm;
      width: 210mm;
    }
  }
  .doc-header {
    align-items: flex-end;
    border-bottom: 2px solid #4737FF;
    display: flex;
    justify-content: space-between;
    margin-bottom: 16pt;
    padding-bottom: 10pt;
  }
  .doc-logo { display: block; height: 22pt; }
  .doc-header-meta {
    color: #6b7280;
    font-size: 8pt;
    line-height: 1.45;
    text-align: right;
  }
  .doc-title {
    color: #111827;
    font-size: 17pt;
    font-weight: 700;
    letter-spacing: -.01em;
    line-height: 1.25;
    margin: 0 0 3pt;
  }
  .doc-subtitle {
    color: #6b7280;
    font-size: 9pt;
    margin: 0 0 14pt;
  }
  .doc-acceptance {
    background: #F2FAF5;
    border: 1px solid #BFE3CD;
    border-left: 3px solid #2FA36B;
    break-inside: avoid;
    font-size: 9pt;
    margin: 0 0 16pt;
    padding: 8pt 10pt;
  }
  .doc-acceptance strong { color: #1F7A4E; }
  .doc-body p {
    hyphens: auto;
    margin: 0 0 7pt;
    orphans: 3;
    text-align: justify;
    widows: 3;
  }
  .doc-body .page-subtitle {
    break-after: avoid;
    color: #111827;
    font-size: 11pt;
    font-weight: 700;
    margin: 14pt 0 6pt;
    page-break-after: avoid;
  }
  .doc-body ul { margin: 0 0 8pt; padding-left: 14pt; }
  .doc-body li { break-inside: avoid; margin: 0 0 3pt; }
  .doc-body strong { color: #111827; font-weight: 600; }
  .doc-body .text-primary { color: inherit; }
  .doc-body a {
    color: #4737FF;
    hyphens: none;
    text-decoration: none;
    white-space: nowrap;
  }
  .doc-footer {
    border-top: 1px solid #e5e7eb;
    color: #6b7280;
    display: flex;
    font-size: 8pt;
    justify-content: space-between;
    margin-top: 18pt;
    padding-top: 8pt;
  }
`;

export function buildTermsPrintHtml({
  title,
  updatedLabel,
  contentHtml,
  logoUrl = null,
  acceptance = null,
  printedAt = '',
}) {
  const logo = logoUrl
    ? `<img class="doc-logo" src="${escapeHtml(logoUrl)}" alt="Stenci">`
    : '<strong>Stenci</strong>';

  const acceptanceBlock = acceptance
    ? `<div class="doc-acceptance">Aceito em <strong>${escapeHtml(acceptance.date)}</strong>`
      + `${acceptance.name ? ` por <strong>${escapeHtml(acceptance.name)}</strong>` : ''}`
      + `${acceptance.email ? ` (${escapeHtml(acceptance.email)})` : ''}.</div>`
    : '';

  return `<!doctype html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>${PRINT_STYLES}</style>
</head>
<body>
<div class="sheet">
  <header class="doc-header">
    ${logo}
    <div class="doc-header-meta">
      STENCI TECNOLOGIA LTDA<br>
      CNPJ 52.096.345/0001-94
    </div>
  </header>
  <h1 class="doc-title">${escapeHtml(title)}</h1>
  <p class="doc-subtitle">${escapeHtml(updatedLabel)}</p>
  ${acceptanceBlock}
  <main class="doc-body">${contentHtml}</main>
  <footer class="doc-footer">
    <span>${escapeHtml(title)}</span>
    <span>Impresso em ${escapeHtml(printedAt)}</span>
  </footer>
</div>
</body>
</html>`;
}

export default function printTerms(options) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const doc = frameWindow.document;
  doc.open();
  doc.write(buildTermsPrintHtml(options));
  doc.close();

  let printed = false;
  const run = () => {
    if (printed) return;
    printed = true;
    frameWindow.focus();
    frameWindow.print();
    setTimeout(() => iframe.remove(), 1000);
  };

  const logo = doc.querySelector('img');
  if (logo && !logo.complete) {
    logo.addEventListener('load', run);
    logo.addEventListener('error', run);
    setTimeout(run, 1500);
  } else {
    setTimeout(run, 50);
  }
}
